import Mention from '@tiptap/extension-mention';

/**
 * One node, two shapes.
 *
 * A person mention and a record mention are the same thing to type and a
 * different thing to store, so they share a node and part on the way out:
 *
 *   person  <mark class="comment-mention" data-id="<user id>">@Dana</mark>
 *   record  <span data-type="mention" data-href="/projects/…">@Q3 Gifting</span>
 *
 * The <mark> is not decoration. It is what CommentsSection reads back to
 * decide which comment_mentions rows to insert, which is what fires
 * comment_mentions_notify() and puts the thread in somebody's Inbox. Delete
 * the pill from the box and the row is never written — which is the behaviour
 * people expect and the old composer did not have: it matched "@Name" as TEXT,
 * so editing the name by hand left a notification pointing at a name the
 * comment no longer contained.
 *
 * ── Why <mark> and not another <span> ──────────────────────────────────────
 *
 * Comments written before this editor existed are plain text, and
 * renderCommentHtml wraps each known person's name in exactly this tag at
 * render time. Using the same tag means an old comment and a new one look
 * identical in a thread, and one CSS rule styles both.
 */

export type MentionScope = 'person' | 'record';

function scopeOf(value: unknown): MentionScope {
  return value === 'person' ? 'person' : 'record';
}

export const CommentMention = Mention.extend({
  name: 'mention',

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-id'),
        renderHTML: () => ({}),
      },
      label: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute('data-label') ?? element.textContent?.replace(/^@/, ''),
        renderHTML: () => ({}),
      },
      scope: {
        default: 'record',
        // Read from the tag rather than from an attribute of its own. The tag
        // IS the distinction — a <mark> is a person — so a stored comment
        // cannot claim to be one thing while rendering as the other.
        parseHTML: (element: HTMLElement) =>
          element.tagName.toLowerCase() === 'mark' ? 'person' : 'record',
        renderHTML: () => ({}),
      },
      href: {
        default: '',
        parseHTML: (element: HTMLElement) => element.getAttribute('data-href') ?? '',
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'mark[data-id]' }, { tag: 'span[data-type="mention"]' }];
  },

  renderHTML({ node }) {
    const label = String(node.attrs.label ?? '');
    const id = String(node.attrs.id ?? '');
    if (scopeOf(node.attrs.scope) === 'person') {
      return [
        'mark',
        { class: 'comment-mention', 'data-id': id, 'data-label': label },
        `@${label}`,
      ];
    }
    const href = String(node.attrs.href ?? '');
    return [
      'span',
      {
        'data-type': 'mention',
        class: 'comment-mention-pill',
        'data-id': id,
        'data-label': label,
        // Only in-app paths. The sanitizer refuses anything else on the way
        // out too; this stops it entering the document in the first place.
        ...(href.startsWith('/') ? { 'data-href': href } : {}),
      },
      `@${label}`,
    ];
  },

  renderText({ node }) {
    return `@${String(node.attrs.label ?? '')}`;
  },
});
